/**
 * DemoDataBanner — surfaces on the dashboard of any workspace that
 * currently contains demo data (records tagged `is_demo:true`).
 *
 * Behaviour:
 *   • Visible to Owner + Admin only. Other roles never see it.
 *   • Polls `/api/workspace/demo-status` once on mount — cheap query.
 *   • When there is no demo data, the component renders nothing.
 *
 * Copy is verbatim from the Super-Admin Demo Data Generator spec.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Warning } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

export default function DemoDataBanner() {
  const { activeRole } = useAuth();
  const [hasDemo, setHasDemo] = useState(false);

  useEffect(() => {
    if (!["owner", "admin"].includes(activeRole)) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/workspace/demo-status");
        if (mounted) setHasDemo(!!data.has_demo_data);
      } catch {
        // silent — banner is non-critical
      }
    })();
    return () => { mounted = false; };
  }, [activeRole]);

  if (!hasDemo) return null;

  return (
    <div
      role="alert"
      data-testid="demo-data-banner"
      className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md px-4 py-2.5 flex items-start gap-3"
    >
      <Warning size={18} weight="duotone" className="shrink-0 mt-0.5" />
      <div className="text-sm">
        This workspace contains demo data. Use the Super Admin panel to remove it before going live.
      </div>
    </div>
  );
}
